import { MembershipDetailed } from 'memberships/schema'
import React, { useEffect, useState } from 'react'
import { DateFormats, formatISOString } from 'utils/dates'
import InfoRow, { InfoRowContainer } from 'uiComponents/infoRow'
import Section from '../../common/section/section'
import { KebabMenu } from 'uiComponents/menus/kebabMenu'
import { useParams } from 'react-router-dom'
import { BaseRouteParams } from 'hocs'
import CommentField, { Comment } from 'uiComponents/comment/comment'
import { useUpdateMembershipMutation } from 'memberships/reduxQueries'
import { usePermission } from 'admin/hocs'
import { LoaderWrapper } from 'uiComponents/loaders'
import Badge, { BadgeVariant } from 'uiComponents/badge/badge'
import './general.scss'

interface GeneralSectionProps {
    membership?: MembershipDetailed
    isLoading?: boolean
    isLoadingMembershipStatus?: boolean
    openCancelMembershipModal: () => void
}

const GeneralSection = ({
    membership,
    isLoading,
    isLoadingMembershipStatus,
    openCancelMembershipModal,
}: GeneralSectionProps) => {
    const { accountSlug } = useParams<BaseRouteParams>()
    const [updateMembership] = useUpdateMembershipMutation()
    const [comment, setComment] = useState<Comment | undefined>()
    const { hasPermission } = usePermission()

    let membershipStatusText,
        memberSinceText,
        currentValidPeriodText = ''
    let membershipStatusVariant: BadgeVariant | undefined

    if (membership) {
        const membershipExpiryDate = formatISOString(membership.expiresAt, DateFormats.SHORT_DATE)
        if (membership.status === 'inactive') {
            membershipStatusText = `Expired on ${membershipExpiryDate}`
            membershipStatusVariant = 'error'
        } else if (membership.autoRenewEnabled) {
            membershipStatusText = `Renews on ${membershipExpiryDate}`
            membershipStatusVariant = 'success'
        } else {
            membershipStatusText = `Expires on ${membershipExpiryDate}`
            membershipStatusVariant = 'warning'
        }

        memberSinceText = formatISOString(membership.createdAt, DateFormats.EXTRA_LONG_DATE)

        currentValidPeriodText = `${formatISOString(
            membership.createdAt,
            DateFormats.EXTRA_LONG_DATE,
        )} - ${formatISOString(membership.expiresAt, DateFormats.EXTRA_LONG_DATE)}`
    }

    const menuOptions = [
        ...(hasPermission('refund_orders') && membership?.status !== 'inactive'
            ? [
                  {
                      label: 'Cancel membership',
                      onClick: openCancelMembershipModal,
                  },
              ]
            : []),
        {
            label: 'Go to order',
            link: `/account/${accountSlug}/orders/${membership?.orderUuid}/details`,
        },
        {
            label: 'Go to product',
            link: `/account/${accountSlug}/products/crud/${membership?.article.uuid}`,
        },
    ]

    const handleSetComment = async (content: string) => {
        if (!accountSlug || !membership) return

        setComment({
            content,
            uuid: comment?.uuid || undefined,
        })

        await updateMembership({
            accountSlug,
            uuid: membership.uuid,
            overrides: {
                comment: {
                    content,
                    uuid: comment?.uuid || undefined,
                },
            },
        })
    }

    useEffect(() => {
        if (membership) {
            setComment(membership.comments[0])
        }
    }, [membership])

    return (
        <Section className="general">
            <div className="action-row">
                <Badge
                    className="renewal-status-badge"
                    variant={membershipStatusVariant}
                    loading={isLoadingMembershipStatus}
                >
                    {membershipStatusText}
                </Badge>
                <KebabMenu options={menuOptions} disabled={isLoading} listAnchor="right" />
            </div>
            <InfoRowContainer loading={isLoading}>
                <InfoRow label="Membership ID" className="membership-id" value={membership?.membershipId} copyable />
                <InfoRow label="Barcode" className="barcode" value={membership?.barcode} copyable />
                <InfoRow label="Member since" className="member-since" value={memberSinceText} />
                <InfoRow label="Current valid period" className="current-valid-period" value={currentValidPeriodText} />
            </InfoRowContainer>
            <div className="notes-container">
                <LoaderWrapper loading={isLoading}>
                    <CommentField comment={comment} isFetching={false} onHandleSetComment={handleSetComment} />
                </LoaderWrapper>
            </div>
        </Section>
    )
}

export default GeneralSection
